<script setup lang="ts">
import { Card } from '@Shared/types/portal/dashboard';
import { onMounted, onUnmounted, ref } from 'vue';
import { useScreen } from '@Shared/utils/useScreen';
import VerbatimCard from './parts/VerbatimCard.vue';
import SkeletonVerbatimCard from './skeletons/parts/VerbatimCard.vue';

const props = withDefaults(
  defineProps<{
    title: string;
    cards: Card[];
    durationInMilliseconds: number;
    cardTemplate: any;
  }>(),
  {
    durationInMilliseconds: 10000,
    cardTemplate: VerbatimCard,
  }
);

/**
 * == Generation of styling for css cards ==
 * This is quite complex, because we want *smooth* animations that don't jump.
 *
 * The rough idea is as follows;
 * - We have a 1x2 grid, this lets us place the cards in either the left or right grid item.
 * - The left grid item has a simple animation that just moves it from the right -> left
 *   - and then back to the right when it's time for the next card.
 * - The right grid items have a series of animations for each individual card
 *   that describes how it animates to the next position
 *
 * The complexity here is that the number of cards shown on the right is variable and not constant.
 * we show up to a max of 5 cards on the right (configurable in code, and maybe later in config).
 * This is not even including the fact that breakpoints mean we need to change the way we size cards
 * respectively...
 *
 * This makes it *feel* extremely complicated to implement but in actuality it's quite simple.
 * We use a mixture of SASS generation + Vue3 Styling Variables to offload the vast majority of the
 * complexity to code (not styling) and just have styling be generic.
 *
 * I've arguably probably over-commented this code just due to the complexity.
 *
 * Something to note is how we handle cards counts larger than our maximum (of 5 by default).
 * What we want to do is make the card 'color' changing effect extremely subtle.
 * - Make the old last card disappear behind the second last one
 * - Giving room to slide the left card to the very right
 * - Every other card on the right shuffles to the left leaving a 'new' card
 *   as the top of the second right-most card.
 */

// == Code ==

// the active card is the card that's moving from right -> left -> right.
const activeCardKey = ref<string | null>(null);
const inactiveCardKey = ref<string | null>(null);

// max cards to ever show (it'll be sliced to be within this limit)
const maxCards = ref<number>(10000);

// Maximum number of cards that are shown at any point in time
const maxCardsToVisiblyShow = ref<number>(5);

// We maintain an internal list of cards that we slice to act as a rotating list.
const internalCards = ref<Card[]>(props.cards);
const isAnimationDisabled = ref<boolean>(JSON.parse(import.meta.env.VITE_DISABLE_ANIMATION));

// This marks the first card as active (which is the top card on the right)
// then moves it to the end of the array, and after a timeout unmarks it as active.
const moveCards = () => {
  if (!internalCards.value || internalCards.value.length <= 2) return;

  inactiveCardKey.value = activeCardKey.value;
  activeCardKey.value = internalCards.value[0].idField;
  updateClasses();

  const tmp = internalCards.value.slice(1);
  tmp.push(internalCards.value[0]);
  internalCards.value = tmp;
};

// Everytime the screen changes we animate, this is to avoid the cards getting stuck in weird positions.
const screen = useScreen();

// This is the main timer that drives the movement of cards
const moveCardsTimer = ref<number | undefined>(undefined);

onMounted(() => {
  if (props.durationInMilliseconds > -1 && !isAnimationDisabled.value) {
    // when debugging animations it's often easier to comment out this line so that they don't move on you every so often.
    // @NOTE: This doesn't speed up as the other intervals speed up because it's animation based
    moveCardsTimer.value = window.setInterval(moveCards, props.durationInMilliseconds);
  }

  moveCards();
});

onUnmounted(() => {
  clearInterval(moveCardsTimer.value);
});

interface CardStyleVariables {
  scaleY?: string;
  oldScaleY?: string;

  marginRight?: string;
  oldMarginRight?: string;
}

const gap = computed<string>(() => {
  let gap = 2;
  if (screen.value.width >= 3840) {
    gap = 6;
  } else if (screen.value.width >= 1920) {
    gap = 3;
  }

  return `${gap}rem`;
});

// There are 4 types of cards to generate
// Active, this is the card moving from right -> left -> right
// Overflow, these cards don't move since even though we show them they are hidden behind the second last card.  These don't have any animations.
// InActive, this is a card that used to be active but now is the right most card, now needs an animation to become overflow (in some cases)
// 'Normal', this is just one of the normal cards on the right that make their way to become active.
const scale = computed<number>(() => {
  let scale = 1;
  if (screen.value.width >= 3840) {
    scale = 2.5;
  } else if (screen.value.width >= 1920) {
    scale = 1.25;
  }

  return scale;
});

const inactiveCardStyle = computed<CardStyleVariables>(() => {
  return {
    scaleY: String(1 - 0.05 * (Math.min(maxCardsToVisiblyShow.value, internalCards.value.length - 1) - 1)),
  };
});

// Every card from right -> left has increasing margin (by 1rem each time)
// this is scaled by 'scale', so in 1920 it's 1.25rem and in 3840 it's 2.5rem
const normalCardStyles = computed<CardStyleVariables[]>(() => {
  // determine how many cards we have care about, ignoring 1 card (since it's our moving card)
  const numberOfCards = Math.min(maxCardsToVisiblyShow.value, internalCards.value.length - 1);
  // start at right most and move to the left
  let items: CardStyleVariables[] = [];
  const ratio = 0.05;

  for (let i = 1; i < numberOfCards; i++) {
    // we'll reverse at the end
    items.push({
      // normal cards talk about how they move from their position to the next one
      oldMarginRight: `${(i - 1) * scale.value}rem`,
      marginRight: `${i * scale.value}rem`,
      oldScaleY: i > 1 ? items[items.length - 1].scaleY : inactiveCardStyle.value.scaleY, // we lose 10% for each shift
      scaleY: `${1 - Math.max(0, ratio * (numberOfCards - 1 - i))}`, // we lose 10% for each shift
    });
  }

  // this is for the SFC style bindings that need this to exist
  items = items.reverse();

  // we just need items for the rest so that we don't try to do operations on undefined
  for (let i = numberOfCards; i < maxCardsToVisiblyShow.value; i++) {
    items.push({});
  }
  return items;
});

const secondLastCardStyle = computed<CardStyleVariables>(() => {
  return normalCardStyles.value[normalCardStyles.value.length - 2];
});

// If props.cards updates then we want to refresh the entire display to first steps.
// This does make the reload animation a bit sudden/janky, and we could improve this in future.
watch(
  props,
  () => {
    internalCards.value = props.cards.slice(0, maxCards.value);
    activeCardKey.value = null;
    inactiveCardKey.value = null;
    moveCards();
    updateClasses();
  },
  {
    deep: true,
  }
);

const findClass = (card: Card): string => {
  const offset = internalCards.value.indexOf(card);

  if (internalCards.value.length <= 2 && offset == 0) return 'initial-active-card';
  if (internalCards.value.length == 2 && offset == 1) return 'last-card-full';

  if (inactiveCardKey.value == null) {
    // set initial positions for everything, in these cases the 'activeCard' is the first card
    if (card.idField == activeCardKey.value) return 'initial-active-card';
    if (offset == Math.min(maxCardsToVisiblyShow.value + 1, internalCards.value.length) - 2) return 'last-card';
    if (offset > maxCardsToVisiblyShow.value - 2) return 'overflow-card';
    return `initial-normal-card-${offset}`;
  }

  if (card.idField == activeCardKey.value) return 'active-card';
  if (card.idField == inactiveCardKey.value) return 'inactive-card';
  if (offset > maxCardsToVisiblyShow.value - 2) return 'overflow-card';
  return `normal-card-${offset}`;
};

const classes = ref<string[]>([]);

const updateClasses = () => {
  const newClasses = [];
  for (const card of internalCards.value) {
    newClasses.push(findClass(card));
  }
  classes.value = newClasses;
};

watch(
  [internalCards, activeCardKey, inactiveCardKey],
  () => {
    updateClasses();
  },
  {
    deep: true,
    immediate: true,
  }
);
</script>
<template>
  <div class="overflow-hidden mt-2 2xl:mt-4 3xl:mt-8 mb-6 2xl:mb-10 3xl:mb-24 grid grid-cols-2 grid-rows-1 flex-grow">
    <component
      :id="'TopVisualCard-' + card.idField"
      :is="cardTemplate"
      v-for="(card, i) in internalCards"
      :style="`grid-area: 1/2; z-index: ${internalCards.length - internalCards.indexOf(card)};`"
      :card="card"
      :zIndex="internalCards.length - internalCards.indexOf(card)"
      :class="
        card.idField == activeCardKey ? 'active-card' : card.idField == inactiveCardKey ? 'inactive-card' : classes[i]
      "
      :key="card.idField"
    />
    <skeleton-verbatim-card
      v-if="internalCards.length == 1"
      :card="{ id: '-empty-card-internal-ref', hasNoData: true }"
      style="grid-area: 1/2; z-index: 0"
      :zIndex="1"
      class="last-card-full"
    ></skeleton-verbatim-card>
  </div>
</template>
<style scoped lang="scss">
// == Simple styles ==
.active-card {
  // active cards always show at the very top
  // theoretically the maximum would be INT_MAX (i.e. (2^32) - 1 = 2147483647)
  // but for a purely performance reason, I'm capping it at the maximum possible cards it can show
  // per day... which is 10s per card resulting in 8,640 cards per day, which I'm rounding up to 10k
  z-index: v-bind('maxCards') !important;
  // fake grid-gap basically
  padding-right: v-bind('gap');
}

.overflow-card {
  // in future we could optimise them by not showing them
  // (it's a bit more complicated then just a display none,
  // since we need to handle having the top card become normal)

  // just display as a second last card
  transform: scale(v-bind('secondLastCardStyle.scaleY'));
  margin-right: v-bind('secondLastCardStyle.marginRight');

  display: none;
}

/*
  The rough idea of the code below is pretty simple
  - The JS code will handle breakpoints/variables for each card
 */

.active-card {
  animation: active-card 2s ease both;
}

.initial-active-card {
  transform: translateX(-100%) scaleY(1);
  margin-right: 0rem;
  padding-right: v-bind('gap');
}

@keyframes active-card {
  // we start on the right side
  0% {
    // active cards don't have any scale/translate
    transform: translateX(calc(0% - v-bind('gap'))) scaleY(1);
  }

  // at the end of the animation
  // we end up on the left waiting to be sent back to the right
  100% {
    transform: translateX(-100%) scaleY(1);
  }
}

.last-card {
  margin-right: 0rem;
  transform: translateX(0%) scaleY(v-bind('inactiveCardStyle.scaleY'));
}

.last-card-full {
  margin-right: 0rem;
}

.inactive-card {
  z-index: 0;
  animation: inactive-card 3s ease both;
}

@keyframes inactive-card {
  // we start on the right side
  0% {
    // active cards don't have any scale/translate
    transform: translateX(calc(-100% + v-bind('gap'))) scaleY(1);
  }

  // delay moving to the left for 20%
  20% {
    transform: translateX(calc(-100% + v-bind('gap'))) scale(v-bind('inactiveCardStyle.scaleY'));
  }

  // to avoid the weird squishing look
  90% {
    transform: translateX(0%) scale(v-bind('inactiveCardStyle.scaleY'));
  }

  // at the end of the animation
  // we end up on the left waiting to be sent back to the right
  100% {
    transform: translateX(0%) scaleY(v-bind('inactiveCardStyle.scaleY'));
  }
}

// We can't rely on vue's SFC v-bind pickup code, since it runs too early
// we can rely on it's variable rewriting code since that runs *after* postcss
// thus we can generate v-binds like this, but the code that picks them up
// runs precss (before sass) meaning we can't use sass variables.
@function sassVariableRename($char, $other) {
  @return ('v-bind' + "('normalCardStyles[#{$char}].#{$other}')");
}

@for $i from 0 through 3 {
  .normal-card-#{$i} {
    animation: normal-card-#{$i} 2s ease both;
    margin-right: #{sassVariableRename($i, 'marginRight')};
    transform: scaleY(#{sassVariableRename($i, 'scaleY')});
  }

  .initial-normal-card-#{$i} {
    margin-right: #{sassVariableRename($i, 'marginRight')};
    transform: scaleY(#{sassVariableRename($i, 'scaleY')});
  }

  @keyframes normal-card-#{$i} {
    0% {
      transform: scaleY(#{sassVariableRename($i, 'oldScaleY')})
        translateX(calc(#{sassVariableRename($i, 'marginRight')} - #{sassVariableRename($i, 'oldMarginRight')}));
    }

    40% {
      transform: scaleY(#{sassVariableRename($i, 'scaleY')});
    }

    100% {
      transform: scaleY(#{sassVariableRename($i, 'scaleY')});
    }
  }
}

// Sadly vue runs it's SFC code to pickup bindings *before* SASS runs
// this sadly means that we need to list all variables here.
// This is quite ugly, but does solve having to list out each class manually...
.force-vue-to-pickup-bindings {
  left: v-bind('normalCardStyles[0].marginRight');
  left: v-bind('normalCardStyles[0].oldMarginRight');
  left: v-bind('normalCardStyles[0].oldScaleY');
  left: v-bind('normalCardStyles[0].scaleY');
  left: v-bind('normalCardStyles[1].marginRight');
  left: v-bind('normalCardStyles[1].oldMarginRight');
  left: v-bind('normalCardStyles[1].oldScaleY');
  left: v-bind('normalCardStyles[1].scaleY');
  left: v-bind('normalCardStyles[2].marginRight');
  left: v-bind('normalCardStyles[2].oldMarginRight');
  left: v-bind('normalCardStyles[2].scaleY');
  left: v-bind('normalCardStyles[2].oldScaleY');
  left: v-bind('normalCardStyles[3].marginRight');
  left: v-bind('normalCardStyles[3].oldMarginRight');
  left: v-bind('normalCardStyles[3].oldScaleY');
  left: v-bind('normalCardStyles[3].scaleY');
}
</style>
